/* eslint-disable arrow-body-style */
import { Injectable } from "@angular/core";
import { FormControl, ValidatorFn } from "@angular/forms";
import * as moment from "moment-timezone";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { CheckboxQuestion } from "../../../../shared/components/dynamic-form/question-checkbox";
import {
  DropdownOption,
  DropdownQuestion,
  dropdownOptions,
} from "../../../../shared/components/dynamic-form/question-dropdown";
import { FileQuestion } from "../../../../shared/components/dynamic-form/question-file";
import { TextAreaQuestion } from "../../../../shared/components/dynamic-form/question-textarea";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { FORMAT } from "../../../../shared/constants/formats";
import { DEFAULT_QUERY_PARAMS } from "../../../../shared/constants/services";
import { UserInfo } from "../../../../shared/types/user-info";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { AssetService } from "../../assets/asset.service";
import { Asset, AssetCoverageStatuses } from "../../assets/asset.type";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { ContractService } from "../../contracts/contract.service";
import { Contract } from "../../contracts/contract.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { ticketFailureTypes, ticketPriorities, ticketStatuses } from "../ticket";
import { Ticket } from "../ticket.type";

@Injectable()
export class CreateTicketService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private ASSETS_AMOUNT = 50;
  constructor(
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private assetService: AssetService,
    private contractService: ContractService,
  ) {}

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  getCompanies(query?: { partnerId?: string; name?: string }): Observable<DropdownOption[]> {
    const { partnerId, ...filters } = query;
    return this.companyService
      .getCompanies({ where: { partnerId, ...setFilterCondition(filters, "$cont") } })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }

  getAssets(query?: { companyId: string; serialNumber?: string }): Observable<DropdownOption[]> {
    const pagination = { ...DEFAULT_QUERY_PARAMS.pagination, pageSize: this.ASSETS_AMOUNT };
    const { companyId, ...filters } = query;
    return this.assetService
      .getAssets({
        where: {
          companyId,
          contractId: { $not: null },
          ...setFilterCondition(filters, "$cont"),
        },
        pagination,
      })
      .pipe(
        map((data: PaginatedResponse<Asset>) =>
          data.items.map((asset) => {
            return {
              value: asset.id,
              label: asset.serialNumber,
              disabled: asset.coverageStatus !== AssetCoverageStatuses.active,
            };
          }),
        ),
      );
  }

  getContracts(query?: { assetId?: string }): Observable<DropdownOption[]> {
    const { assetId, ...filters } = query;
    return this.contractService
      .getContracts({
        where: {
          ...(assetId ? { contractAssets: { assetId } } : {}),
          ...setFilterCondition(filters, "$cont"),
        },
      })
      .pipe(
        map((r: PaginatedResponse<Contract>) =>
          r.items.map((c: Contract) => ({ value: c.id, label: c.numberVersion })),
        ),
      );
  }

  generateQuestions(
    options: {
      ticket?: Ticket;
      companyValue?: string;
      partnerValue?: string;
      partnerSkip?: boolean;
      companySkip?: boolean;
      assetSkip?: boolean;
      onboardingSkip?: boolean;
    } = {},
  ): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      !options?.partnerSkip &&
        new DropdownQuestion({
          value: options?.partnerValue,
          key: "partnerId",
          label: "Partner",
          currentOption: {
            label: options.ticket?.asset?.company?.partner?.name,
            value: options.ticket?.asset?.company?.partner?.id,
          },
          asyncOptions: this.getPartners(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          required: true,
          order: 1,
        }),

      !options?.companySkip &&
        new DropdownQuestion({
          value: options?.companyValue,
          key: "companyId",
          label: "Company",
          currentOption: {
            label: options.ticket?.asset?.company?.name,
            value: options.ticket?.asset?.company?.id,
          },
          asyncOptions: options?.partnerValue && this.getCompanies({ partnerId: options?.partnerValue }),
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) => this.getCompanies({ partnerId }),
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: ({ parentValue: partnerId, value: name }) => this.getCompanies({ partnerId, name }),
            },
          },
          required: true,
          order: 2,
        }),

      !options?.assetSkip &&
        new DropdownQuestion({
          key: "assetId",
          label: "Asset",
          currentOption: {
            label: options.ticket?.asset?.name,
            value: options.ticket?.asset?.id,
          },
          asyncOptions: options?.companyValue && this.getAssets({ companyId: options?.companyValue }),
          dependsOn: {
            key: "companyId",
            callback: ({ parentValue: companyId }) => this.getAssets({ companyId }),
          },
          search: {
            filter: {
              parentKey: "companyId",
              callback: ({ parentValue: companyId, value: serialNumber }) =>
                this.getAssets({ companyId, serialNumber }),
            },
          },
          required: true,
          order: 3,
        }),

      !options?.assetSkip &&
        new TextboxQuestion({
          key: "assetOem",
          label: "Asset OEM",
          required: false,
          disabled: true,
          hide: true,
          order: 4,
        }),

      !options?.assetSkip &&
        new TextboxQuestion({
          key: "assetModel",
          label: "Asset Model #",
          required: false,
          disabled: true,
          hide: true,
          order: 5,
        }),

      !options?.assetSkip &&
        new TextboxQuestion({
          key: "assetType",
          label: "Asset Type",
          required: false,
          disabled: true,
          hide: true,
          order: 6,
        }),

      new TextboxQuestion({
        value: options?.ticket && options.ticket.subject,
        key: "subject",
        label: "Subject",
        required: true,
        order: 7,
      }),

      new TextAreaQuestion({
        value: options?.ticket && options.ticket.description,
        key: "description",
        label: "Problem Description",
        required: true,
        order: 8,
      }),

      new DropdownQuestion({
        key: "failureType",
        label: "Failure Type",
        options: ticketFailureTypes.filter((type) => !type.hidden),
        // Prevents showing "Memory" type while creating a ticket because it was deprecated.
        // It remains in failure types as there are existing tickets with Memory type
        // and it is not possible to update them in Zendesk. It is used only for readonly fields.
        required: true,
        order: 9,
      }),

      new DropdownQuestion({
        key: "priority",
        label: "Priority",
        options: ticketPriorities,
        required: true,
        order: 10,
      }),

      !options?.onboardingSkip &&
        new DropdownQuestion({
          key: "onboarding",
          options: dropdownOptions.yesNo,
          label: "Onboarding",
          required: true,
          order: 11,
        }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateManageQuestions(
    options: {
      ticket?: Ticket;
      onboardingSkip?: boolean;
    } = {},
  ): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options?.ticket.zendeskId.toString(),
        key: "zendeskId",
        label: "ID",
        order: 1,
        disabled: true,
      }),
      new TextboxQuestion({
        value: options?.ticket.subject,
        key: "subject",
        label: "Subject",
        order: 2,
        disabled: true,
      }),
      new TextboxQuestion({
        value: ticketPriorities.find((p) => p.value === options?.ticket.priority)?.label,
        key: "priority",
        label: "Priority",
        disabled: true,
        order: 3,
      }),
      new TextboxQuestion({
        value: ticketStatuses.find((s) => s.value === options?.ticket.status).label,
        key: "status",
        label: "Status",
        disabled: true,
        order: 4,
      }),
      new TextboxQuestion({
        value: moment(options?.ticket.openedAt).tz("America/New_York").format(FORMAT.DATE_TIME),
        key: "openAt",
        label: "Open Date",
        disabled: true,
        order: 5,
      }),
      new TextboxQuestion({
        value: options?.ticket.solvedAt
          ? moment(options?.ticket.solvedAt).tz("America/New_York").format(FORMAT.DATE_TIME)
          : "",
        key: "solvedAt",
        label: "Solved Date",
        disabled: true,
        order: 6,
      }),
      new TextAreaQuestion({
        value: `${options?.ticket.requester.email}${
          options?.ticket.requester.name ? "\n(" + options?.ticket.requester.name + ")" : ""
        }`,
        key: "requester",
        label: "Requester",
        disabled: true,
        order: 7,
      }),
      new TextboxQuestion({
        value: options?.ticket.asset?.company?.name,
        key: "company.name",
        label: "Company Name",
        disabled: true,
        order: 8,
      }),
      new TextboxQuestion({
        value: options?.ticket.asset?.serialNumber,
        key: "serialNumber",
        label: "Serial #",
        disabled: true,
        order: 9,
      }),
      new TextboxQuestion({
        value: ticketFailureTypes.find((t) => t.value === options?.ticket.failureType)?.label,
        label: "Failure Type",
        key: "failureType",
        disabled: true,
        order: 10,
      }),
      new DropdownQuestion({
        value: options?.ticket.contractId,
        label: "Contract #",
        key: "contractId",
        currentOption: {
          label: options.ticket?.contract?.name,
          value: options.ticket?.contract?.id,
        },
        asyncOptions: this.getContracts({ assetId: options?.ticket.asset?.id }),
        order: 11,
      }),
      new TextAreaQuestion({
        value: options?.ticket.description,
        key: "description",
        label: "Description",
        disabled: true,
        order: 12,
      }),
      ...(options.ticket.solvedAt
        ? [
            new TextAreaQuestion({
              value: options?.ticket.resolutionNotes,
              key: "resolutionNotes",
              label: "Resolution Notes",
              disabled: true,
              order: 13,
            }),
          ]
        : []),
      !options?.onboardingSkip &&
        new DropdownQuestion({
          key: "onboarding",
          options: dropdownOptions.yesNo,
          value: options?.ticket.onboarding,
          label: "Onboarding",
          disabled: true,
          order: 14,
        }),
    ];

    return of(questions.sort((a, b) => a.order - b.order));
  }

  generateCommentQuestions(user: UserInfo): Observable<QuestionBase<string | boolean>[]> {
    const maxSizeValidatorPerFile = (size: number): ValidatorFn => {
      return (c: FormControl) => {
        if (Array.isArray(c.value)) {
          const filteredFiles = c.value.filter((f: File) => f.size > size);
          return filteredFiles.length > 0 ? { maxSize: filteredFiles.length } : null;
        }
        return null;
      };
    };

    type ReturnType = (size: number) => string;
    const formatSize = (): ReturnType => {
      return (size: number) => {
        const mb = size / 1024 / 1024;
        return `${mb.toFixed(2)} MB`;
      };
    };

    const maxSize = 50 * 1024 * 1024;
    const questions: QuestionBase<string | boolean>[] = [
      new TextAreaQuestion({
        key: "comment",
        label: "Comment",
        maxLength: 500,
        required: true,
        order: 1,
      }),
      new FileQuestion({
        key: "attachments",
        fileOptions: {
          multiple: true,
        },
        onlyShowUploadButton: true,
        chipList: {
          bind: true,
          uniqueKey: ["name", "lastModified"],
          labelKeys: [
            {
              key: "name",
            },
            { key: "size", format: formatSize() },
          ],
        },
        order: 2,
        errors: [
          {
            key: "maxSize",
            validator: maxSizeValidatorPerFile(maxSize),
            message: "The file size limit is 50 MB",
          },
        ],
      }),
      new CheckboxQuestion({
        key: "isPublicInternal",
        label: "Hide this comment from company users",
        value: false,
        order: 3,
        hide: user.isCompany() || user.isGlobal(),
      }),
    ];

    return of(questions.sort((a, b) => a.order - b.order));
  }
}
